import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api from '../services/authService';
import { useAuth } from './AuthContext';
import enMessages from '../locales/en.json';
import arMessages from '../locales/ar.json';

type SupportedLanguage = 'en' | 'ar';

interface LanguageContextType {
  language: SupportedLanguage;
  direction: 'ltr' | 'rtl';
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation dictionaries loaded from JSON files - easy to extend
const translations: Record<SupportedLanguage, Record<string, string>> = {
  en: enMessages as Record<string, string>,
  ar: arMessages as Record<string, string>,
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
};

interface Props {
  children: ReactNode;
}

export const LanguageProvider: React.FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const [language, setLanguageState] = useState<SupportedLanguage>('en');

  // Direction information is available for components that want custom RTL handling,
  // but we do not change the global document direction to avoid disturbing layout.
  const direction: 'ltr' | 'rtl' = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    const userLang = (user as any)?.language as SupportedLanguage | undefined;
    if (userLang && (userLang === 'en' || userLang === 'ar')) {
      setLanguageState(userLang);
    }
  }, [user?.id, (user as any)?.language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = async (lang: SupportedLanguage) => {
    setLanguageState(lang);
    try {
      await api.post('/me/language', { language: lang });
    } catch (e) {
      console.error('Failed to update language preference', e);
    }
  };

  const t = (key: string): string => {
    const dict = translations[language] || translations.en;
    return dict[key] ?? translations.en[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, direction, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};


